import * as Yup from 'yup';

const phoneRegex = /^([+]1)?[(]?[2-9][0-9]{2}[)]?[-\s.]?[2-9][0-9]{2}[-\s.]?[0-9]{4}$/im;

const MIN_PICKUP_PREPARATION_MINUTES = 10;
const PICKUP_PREPARATION_MIN_MESSAGE = `Pickup preparation time must be at least ${MIN_PICKUP_PREPARATION_MINUTES} minutes.`;
const PICKUP_ETA_MIN_MESSAGE = 'ETA cannot be less than 1!';
const MIN_DELIVERY_ETA_GAP_MINUTES = 10;
const PREP_VS_DELIVERY_MESSAGE = `Prep time must be at least ${MIN_DELIVERY_ETA_GAP_MINUTES} min shorter than delivery time. Need help? Contact support.`;

// The 10-minute minimum only applies while Pickup is on: with Pickup off the
// field is disabled, so an older lower value could not be fixed and would block
// saving the rest of the settings.
const isPickupOn = (deliveryOptions) => Boolean(deliveryOptions?.Pickup);

const StoreSettingsSchema = Yup.object()
  .shape({
    FullName: Yup.string()
      .required('Please enter location name')
      .trim()
      .max(50, 'Location name cannot be more than 50 characters'),
    Phone: Yup.string()
      .required('Please enter phone')
      .matches(phoneRegex, 'Please enter valid phone'),
    Address: Yup.string()
      .required('Please enter location address')
      .trim('Please enter location address'),
    City: Yup.string()
      .required(
        'Your address does not have any city specified. Please select another address or choose address with city specified!'
      )
      .trim('The field cannot contain only spaces.'),
    StreetNumber: Yup.string()
      .required(
        'Your address does not have any street number specified. Please select another address or choose address with street number specified!'
      )
      .trim('The field cannot contain only spaces.'),
    PickupEtaMinutes: Yup.number().when('DeliveryOptions', {
      is: isPickupOn,
      then: Yup.number()
        .typeError(PICKUP_PREPARATION_MIN_MESSAGE)
        .min(MIN_PICKUP_PREPARATION_MINUTES, PICKUP_PREPARATION_MIN_MESSAGE),
      otherwise: Yup.number().min(1, PICKUP_ETA_MIN_MESSAGE)
    }),
    // .positive('ETA cannot be a negative value!'),
    EtaMinutes: Yup.number()
      .min(1, 'ETA cannot be less than 1!')
      .positive('ETA cannot be a negative value!'),
    CountryCode: Yup.string()
      .required('Please enter county code')
      .min(1)
      .max(3),
    TaxPercents: Yup.array().of(
      Yup.object().shape({
        Name: Yup.string()
          .max(20, 'Tax Name cannot contain more than 20 symbols!')
          .required('Please enter tax name!'),
        Value: Yup.string().required('Please enter tax value!')
      })
    ),
    MailForWeeklyReport: Yup.string()
      .nullable()
      .test(
        'is-valid-emails',
        'Please enter valid email(s), separated by commas',
        (value) => {
          if (!value || value.trim() === '') return true; // allow empty
          const emails = value.split(',').map((email) => email.trim());
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emails.every((email) => emailRegex.test(email));
        }
      ),
    MenuBannerText: Yup.string()
      .nullable()
      .trim()
      .max(1000, 'Menu page banner cannot be more than 1000 characters')
    /* MailForHumanResponseMessage: Yup.string()
      .nullable(true)
      .email('Invalid email!'), */
  })
  .test(
    'global-ok',
    'ETA cannot be less than Food Preparation Time',
    function (value) {
      // Only in-house delivery has a fixed delivery time to compare with. The
      // error stays on the prep time field whichever of the two was changed.
      if (
        value.InhouseDelivery &&
        value.EtaMinutes < value.PickupEtaMinutes + MIN_DELIVERY_ETA_GAP_MINUTES
      ) {
        return this.createError({
          path: 'PickupEtaMinutes',
          message: PREP_VS_DELIVERY_MESSAGE
        });
      }

      if (!value.PickupEtaMinutes) {
        return this.createError({
          path: 'PickupEtaMinutes',
          message: isPickupOn(value.DeliveryOptions)
            ? PICKUP_PREPARATION_MIN_MESSAGE
            : PICKUP_ETA_MIN_MESSAGE
        });
      }

      if (value.Address && !value.FullAddress) {
        return this.createError({
          path: 'Address',
          message:
            'Please choose the suggested address from the dropdown or select it on the map!'
        });
      }
      return true;
    }
  );

export default StoreSettingsSchema;
